import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import type { SlideProps } from '@/components/SlideWizard'
import { useSlideCopy } from '@/lib/i18n'
import { copy, siteCopy } from './Slide04AiRevolution.copy'

const ease = [0.16, 1, 0.3, 1] as const
const transitionBase = { duration: 0.45, ease }
const animationProps = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 } }

export default function Slide04AiRevolution(_props: SlideProps) {
  const c = useSlideCopy(copy, { site: siteCopy })

  // Split body around the {trigger} sentinel, the chunk before renders as
  // plain text, the trigger renders as an inline popover-button, the chunk
  // after renders as plain text.
  const [beforeTrigger, afterTrigger] = c.body.split('{trigger}')

  return (
    <section className="text-slate-900 min-h-[70vh] w-full flex flex-col justify-center">
      <motion.div
        {...animationProps}
        transition={{ ...transitionBase, delay: 0 }}
        className="mb-5 max-w-2xl text-center mx-auto"
      >
        <h2 className="text-5xl sm:text-6xl font-semibold text-slate-900 tracking-tight whitespace-pre-line leading-[1.1] text-center">
          {c.title}
        </h2>
      </motion.div>

      <motion.div
        {...animationProps}
        transition={{ ...transitionBase, delay: 0.1 }}
        className="text-xl sm:text-2xl mb-10 leading-relaxed text-slate-600 max-w-3xl text-center mx-auto"
      >
        <p>
          <span>{beforeTrigger}</span>
          <InlinePopover
            triggerText={c.triggerText}
            title={c.popoverTitle}
            body={c.popoverBody}
            sourcePrefix={c.popoverSourcePrefix}
            source={c.popoverSource}
          />
          <span>{afterTrigger}</span>
        </p>
      </motion.div>

      <div className="mt-5 grid gap-6 md:grid-cols-[1.15fr_100px_0.85fr] items-start max-w-4xl mx-auto w-full">
        <motion.div
          {...animationProps}
          transition={{ ...transitionBase, delay: 0.2 }}
          className="overflow-hidden pt-3"
        >
          <OldEmailExample
            windowTitle={c.oldEmailWindowTitle}
            greeting={c.oldEmailGreeting}
            body={c.oldEmailBody}
            signoff={c.oldEmailSignoff}
            signature={c.oldEmailSignature}
          />
          <p className="mt-3 text-sm sm:text-base text-slate-400 text-center">
            {c.oldCaption}
          </p>
        </motion.div>

        <motion.div
          {...animationProps}
          transition={{ ...transitionBase, delay: 0.25 }}
          className="hidden md:flex items-center justify-center self-stretch"
          aria-hidden="true"
        >
          <span className="dir-flip inline-block text-3xl font-light text-slate-400">→</span>
        </motion.div>

        <motion.div
          {...animationProps}
          transition={{ ...transitionBase, delay: 0.35 }}
          className="overflow-hidden pt-3"
        >
          <PhoneSms
            smsSender={c.smsSender}
            smsAvatarInitial={c.smsAvatarInitial}
            smsBody={c.smsBody}
            smsLinkText={c.smsLinkText}
            smsLinkTooltip={c.smsLinkTooltip}
          />
          <p className="mt-3 text-sm sm:text-base text-slate-400 text-center">
            {c.newCaption}
          </p>
        </motion.div>
      </div>
    </section>
  )
}

/* ─── Inline popover (tells / warning signs) ─────────────────── */

type InlinePopoverProps = {
  triggerText: string
  title: string
  body: string
  sourcePrefix: string
  source?: string
}

function InlinePopover({ triggerText, title, body, sourcePrefix, source }: InlinePopoverProps) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!open) return
    const handleDown = (e: MouseEvent | TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleDown)
    document.addEventListener('touchstart', handleDown)
    return () => {
      document.removeEventListener('mousedown', handleDown)
      document.removeEventListener('touchstart', handleDown)
    }
  }, [open])

  return (
    <span
      ref={containerRef}
      className="relative inline"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="inline text-inherit leading-relaxed text-start underline decoration-dotted decoration-slate-400 underline-offset-4 hover:decoration-violet-400 hover:text-slate-700 transition-colors cursor-pointer"
      >
        {triggerText}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.98 }}
            transition={{ duration: 0.25, ease }}
            className="absolute start-0 top-full z-30 mt-3 w-[340px] sm:w-[400px] rounded-2xl bg-white border border-slate-200 shadow-xl shadow-slate-200/60 p-5 sm:p-6 text-start"
          >
            <motion.p
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.08 }}
              className="text-lg font-semibold text-slate-900 mb-2 leading-snug"
            >
              {title}
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.16 }}
              className="text-base text-slate-600 leading-relaxed"
            >
              {body}
            </motion.p>
            {source && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.28 }}
                className="mt-3 text-sm text-slate-400"
              >
                {sourcePrefix}
                {source}
              </motion.p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </span>
  )
}

/* ─── Old email (Windows 98 style) ───────────────────────────── */

type OldEmailProps = {
  windowTitle: string
  greeting: string
  body: string
  signoff: string
  signature: string
}

function OldEmailExample({ windowTitle, greeting, body, signoff, signature }: OldEmailProps) {
  return (
    <div className="bg-[#c0c0c0] border border-slate-600 shadow-[2px_2px_0_#4b5563]">
      <div className="border-t border-s border-white border-e border-b border-[#808080]">
        <div className="flex items-center justify-between bg-[#000080] px-2 py-1 border-b border-[#808080]">
          <div className="flex items-center gap-2">
            <span className="text-xs leading-none">✉</span>
            <span className="text-xs font-bold text-white">{windowTitle}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="inline-flex items-center justify-center w-4 h-4 bg-[#c0c0c0] border border-slate-700 text-[10px] font-bold leading-none">
              –
            </span>
            <span className="inline-flex items-center justify-center w-4 h-4 bg-[#c0c0c0] border border-slate-700 text-[10px] font-bold leading-none">
              □
            </span>
            <span className="inline-flex items-center justify-center w-4 h-4 bg-[#c0c0c0] border border-slate-700 text-[10px] font-bold leading-none">
              ×
            </span>
          </div>
        </div>
        <div className="bg-[#f5f5f5] px-4 py-4 text-slate-900 space-y-3 text-base leading-relaxed border-t border-white border-s border-white border-e border-[#808080] border-b border-[#808080]">
          <p>{greeting}</p>
          <p>{body}</p>
          <p>{signoff}</p>
          <p>{signature}</p>
        </div>
      </div>
    </div>
  )
}

/* ─── Phone SMS mockup ───────────────────────────────────────── */

type PhoneSmsProps = {
  smsSender: string
  smsAvatarInitial: string
  smsBody: string
  smsLinkText: string
  smsLinkTooltip: string
}

function PhoneSms({ smsSender, smsAvatarInitial, smsBody, smsLinkText, smsLinkTooltip }: PhoneSmsProps) {
  return (
    <div className="relative inline-block max-w-sm overflow-hidden">
      <div className="relative rounded-[32px] rounded-b-none border border-black border-b-0 bg-black shadow-lg p-1 pb-0">
        <span className="absolute -start-1 top-10 h-10 w-[3px] rounded-full bg-black" />
        <span className="absolute -start-1 top-24 h-6 w-[3px] rounded-full bg-black" />
        <span className="absolute -start-1 top-36 h-6 w-[3px] rounded-full bg-black" />
        <div className="relative rounded-[26px] rounded-b-none bg-white overflow-hidden border border-black border-b-0">
          <div className="bg-white relative">
            <div className="h-6 flex items-center justify-between px-4 text-[10px] font-semibold text-slate-900">
              <span>9:41</span>
              <div className="flex items-center gap-2">
                <span className="inline-flex gap-[2px] rotate-180 scale-x-[-1]">
                  <span className="w-[2px] h-[6px] bg-slate-900 rounded-sm" />
                  <span className="w-[2px] h-[8px] bg-slate-900 rounded-sm" />
                  <span className="w-[2px] h-[10px] bg-slate-900 rounded-sm" />
                  <span className="w-[2px] h-[12px] bg-slate-900 rounded-sm" />
                </span>
                <span className="relative w-4 h-[10px] border border-slate-900 rounded-[2px]">
                  <span className="absolute start-[1px] top-[1px] h-[6px] w-[9px] bg-slate-900 rounded-[1px]" />
                  <span className="absolute -end-[3px] top-[3px] h-[4px] w-[2px] bg-slate-900 rounded-sm" />
                </span>
              </div>
            </div>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 h-4 w-24 rounded-b-[12px] bg-black" />
            <div className="flex items-center gap-2 px-4 py-1.5 md:py-2 border-b border-slate-200">
              <span className="dir-flip inline-block text-blue-500 text-base md:text-lg leading-none">‹</span>
              <div className="h-5 w-5 md:h-6 md:w-6 rounded-full bg-slate-300 text-[9px] md:text-[10px] font-semibold text-slate-700 flex items-center justify-center">
                {smsAvatarInitial}
              </div>
              <div className="flex-1">
                <p className="text-xs md:text-sm font-semibold text-slate-900 leading-tight">
                  {smsSender}
                </p>
              </div>
            </div>
          </div>
          <div className="px-3 md:px-4 pb-4 md:pb-6 pt-4 md:pt-6">
            <div className="bg-slate-200 text-slate-800 text-sm md:text-base lg:text-lg leading-snug md:leading-tight rounded-2xl px-4 md:px-6 py-3 md:py-4 shadow-sm inline-block">
              <SmsBodyWithVerifyLink body={smsBody} linkText={smsLinkText} tooltip={smsLinkTooltip} />
            </div>
            <span className="block w-3 h-3 bg-slate-200 rotate-45 rounded-[2px] -mt-1 ms-3" />
          </div>
        </div>
      </div>
    </div>
  )
}

function SmsBodyWithVerifyLink({
  body,
  linkText,
  tooltip,
}: {
  body: string
  linkText: string
  tooltip: string
}) {
  const target = linkText.toLowerCase()
  const index = body.toLowerCase().indexOf(target)
  if (index === -1) return <>{body}</>
  const before = body.slice(0, index)
  const matched = body.slice(index, index + target.length)
  const after = body.slice(index + target.length)
  return (
    <>
      {before}
      <VerifyLinkTooltip text={matched} tooltip={tooltip} />
      {after}
    </>
  )
}

/**
 * The "verify now" fake link with a teaching tooltip. The tooltip is wider
 * than the phone mockup and the phone has overflow-hidden ancestors (for its
 * rounded corners), so it's rendered through a portal with fixed positioning
 * to escape the clipping and stacking contexts entirely.
 */
function VerifyLinkTooltip({ text, tooltip }: { text: string; tooltip: string }) {
  const triggerRef = useRef<HTMLSpanElement>(null)
  const [coords, setCoords] = useState<{ left: number; top: number } | null>(null)

  const show = () => {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (rect) setCoords({ left: rect.left + rect.width / 2, top: rect.top - 8 })
  }
  const hide = () => setCoords(null)

  return (
    <span
      ref={triggerRef}
      className="relative inline-flex items-center"
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      <span className="text-blue-600 underline underline-offset-2 cursor-help">{text}</span>
      {coords &&
        createPortal(
          <span
            role="tooltip"
            className="pointer-events-none fixed z-[1000] w-72 max-w-[80vw] -translate-x-1/2 -translate-y-full rounded-lg bg-slate-900 px-3 py-2 text-sm leading-snug text-white shadow-lg"
            style={{ left: coords.left, top: coords.top }}
          >
            {tooltip}
          </span>,
          document.body,
        )}
    </span>
  )
}
